import React, { useState, useRef } from 'react';
import { Paperclip, FileText, Eye, Trash2, Upload, X, Loader } from 'lucide-react';
import { uploadAnexo, getSignedUrl, deleteAnexo, fmtTamanho } from '../lib/storage-fluxo';
import { toast } from '../utils/toast';

// ─────────────────────────────────────────────────────────────────────────────
// Modal reutilizável de anexos PDF de um lançamento.
//
// Props:
//   - lancamento: { id, descricao, anexos?: [...] }
//   - autorEmail: email do user logado (vai pra metadata do upload)
//   - podeEditar: bool — quem só tem `ver` não anexa nem exclui, só visualiza
//   - onChange(anexosAtualizados): callback pra persistir no fluxoCaixa
//   - onFechar()
// ─────────────────────────────────────────────────────────────────────────────

export default function AnexosLancamento({ lancamento, autorEmail, podeEditar = true, onChange, onFechar }) {
  const [enviando, setEnviando] = useState(false);
  const [abrindoPath, setAbrindoPath] = useState(null);
  const [excluindoId, setExcluindoId] = useState(null);
  const inputRef = useRef(null);

  const anexos = Array.isArray(lancamento.anexos) ? lancamento.anexos : [];

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setEnviando(true);
    const novos = [];
    const erros = [];
    for (const file of Array.from(files)) {
      try {
        const meta = await uploadAnexo(file, lancamento.id, autorEmail);
        novos.push(meta);
      } catch (e) {
        erros.push(`${file.name}: ${e.message}`);
      }
    }
    if (novos.length > 0) {
      onChange([...anexos, ...novos]);
      toast.sucesso(`${novos.length} anexo(s) enviado(s).`);
    }
    if (erros.length > 0) {
      toast.erro(erros.join(' · '));
    }
    setEnviando(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleAbrir = async (anexo) => {
    setAbrindoPath(anexo.path);
    try {
      const url = await getSignedUrl(anexo.path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.erro(e.message);
    }
    setAbrindoPath(null);
  };

  const handleExcluir = async (anexo) => {
    if (!window.confirm(`Excluir o anexo "${anexo.nome}"? Esta ação não pode ser desfeita.`)) return;
    setExcluindoId(anexo.id);
    try {
      await deleteAnexo(anexo.path);
      onChange(anexos.filter(a => a.id !== anexo.id));
      toast.sucesso('Anexo excluído.');
    } catch (e) {
      toast.erro(e.message);
    }
    setExcluindoId(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!podeEditar) return;
    handleUpload(e.dataTransfer?.files);
  };

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 20, overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10,
          maxWidth: 580, width: '100%', padding: 20, color: '#2d2640', marginTop: 60,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #eeeaf5' }}>
          <div>
            <h2 style={{ fontSize: 16, margin: 0, color: '#1c1530', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Paperclip size={16} /> Anexos do lançamento
            </h2>
            <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>
              {lancamento.descricao}
            </div>
          </div>
          <button onClick={onFechar} style={{ background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* Drop zone / upload */}
        {podeEditar && (
          <div
            onDrop={onDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
            style={{
              border: '2px dashed #e5e0ef', borderRadius: 8, padding: 20,
              textAlign: 'center', cursor: enviando ? 'wait' : 'pointer',
              background: enviando ? 'rgba(31,111,235,0.06)' : 'transparent',
              marginBottom: 16,
              transition: 'background 0.2s',
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              multiple
              style={{ display: 'none' }}
              onChange={e => handleUpload(e.target.files)}
              disabled={enviando}
            />
            {enviando ? (
              <div style={{ color: '#7dd3fc', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Enviando...
              </div>
            ) : (
              <>
                <Upload size={24} color="#5f5874" style={{ opacity: 0.7 }} />
                <div style={{ fontSize: 13, marginTop: 6, color: '#2d2640' }}>
                  Arraste PDFs aqui ou <span style={{ color: '#1f6feb' }}>clique pra escolher</span>
                </div>
                <div style={{ fontSize: 11, color: '#5f5874', marginTop: 4 }}>
                  Só PDF · máximo 10 MB por arquivo
                </div>
              </>
            )}
          </div>
        )}

        {/* Lista de anexos */}
        {anexos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#5f5874', fontSize: 13 }}>
            <FileText size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
            <div>Nenhum anexo neste lançamento.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {anexos.map(anexo => (
              <div
                key={anexo.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 6,
                  background: '#ffffff', border: '1px solid #eeeaf5',
                }}
              >
                <FileText size={18} color="#ef4444" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#1c1530', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {anexo.nome}
                  </div>
                  <div style={{ fontSize: 11, color: '#5f5874', marginTop: 2 }}>
                    {fmtTamanho(anexo.tamanho)}
                    {anexo.uploaded_by && ` · por ${anexo.uploaded_by}`}
                    {anexo.uploaded_at && ` · ${new Date(anexo.uploaded_at).toLocaleDateString('pt-BR')}`}
                  </div>
                </div>
                <button
                  onClick={() => handleAbrir(anexo)}
                  disabled={abrindoPath === anexo.path}
                  title="Visualizar PDF"
                  style={iconBtnStyle('#1f6feb')}
                >
                  {abrindoPath === anexo.path
                    ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Eye size={14} />}
                </button>
                {podeEditar && (
                  <button
                    onClick={() => handleExcluir(anexo)}
                    disabled={excluindoId === anexo.id}
                    title="Excluir"
                    style={iconBtnStyle('#ef4444')}
                  >
                    {excluindoId === anexo.id
                      ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Trash2 size={14} />}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const iconBtnStyle = (cor) => ({
  background: 'transparent',
  border: `1px solid ${cor}`,
  color: cor,
  borderRadius: 6,
  padding: '6px 8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});
